import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INavLink, Nav } from '@fluentui/react';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { OrbUserContext } from './OrbUserContext';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme } from '../AppGlobals';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';
import { DataContext } from './DataContext';
import { _onRenderNavLink } from './ZedXMainMenu';
import { TruncatedText } from './TruncatedText';
import { IEntityDescription1 } from '../types';

interface IManageProjectsMenu extends IWithErrorHandlingProps {
    onLinkClicked?: () => void;
    entityDescriptionForMenu?: IEntityDescription1;
}

type ContextualInfo = {
    isContextualMenu: boolean;
    selectedLabel?: string;
};

export const ManageProjectsMenu = ({
    entityDescriptionForMenu,
    errorHandling: { clearErrors },
    onLinkClicked
}: IManageProjectsMenu): React.ReactElement => {

    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const { userPermissions: up } = useContext(OrbUserContext);
    const history = useHistory();
    const { pathname } = useLocation();
    const { dataServices } = useContext(DataContext);

    const [navs, setNavs] = useState<INavLink[]>([]);

    // Management OPEN by default
    const [portfolioExpanded, setPortfolioExpanded] = useState(true);
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const [totalPortfolios, setTotalPortfolios] = useState(0);
    const [totalProgrammes, setTotalProgrammes] = useState(0);
    const [totalProjects, setTotalProjects] = useState(0);

    const resetErrors = useCallback(clearErrors, [clearErrors]);
    const hasInitialisedFromRoute = useRef(false);

    /* -------------------------------------------------------
       Parse contextual menu mode
    -------------------------------------------------------- */
    const parseContextualMenu = (path: string): ContextualInfo => {

        if (/^\/a2\/objectives-for-vision\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Objectives' };
        }

        if (/^\/a2\/portfolios-for-objective\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Portfolios' };
        }

        if (/^\/a2\/programmes-for-portfolio\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Programmes' };
        }

        if (
            /^\/a2\/projects-for-portfolio\/[^/]+/.test(path) ||
            /^\/a2\/projects-for-programme\/[^/]+/.test(path)
        ) {
            return { isContextualMenu: true, selectedLabel: 'Projects' };
        }

        if (/^\/a2\/sign-off-for-portfolio\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Portfolio Updates' };
        }

        if (/^\/a2\/sign-off-for-programme\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Programme Updates' };
        }

        if (/^\/a2\/sign-off-for-project\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Project Updates' };
        }

        if (/^\/a2\/work-streams-for-project\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Work streams' };
        }

        if (/^\/a2\/benefits-for-project\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Benefits' };
        }

        if (/^\/a2\/dependencies-for-project\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Dependencies' };
        }

        if (/^\/a2\/milestones-for-work-stream\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Milestones' };
        }

        return { isContextualMenu: false };
    };

    const { isContextualMenu, selectedLabel } = parseContextualMenu(pathname);

    /* -------------------------------------------------------
       Fetch counters
    -------------------------------------------------------- */
    useEffect(() => {
        dataServices.portfolioService.countPortfolios().then(setTotalPortfolios);
        dataServices.programmeService.countProgrammes().then(setTotalProgrammes);
        dataServices.projectService.countProjects().then(setTotalProjects);
    }, [dataServices]);

    /* -------------------------------------------------------
       One-time auto-expand based on initial route
       (Management stays open regardless)
    -------------------------------------------------------- */
    useEffect(() => {
        if (hasInitialisedFromRoute.current || isContextualMenu) return;

        if (
            pathname.startsWith('/a2/settings/')
        ) {
            setSettingsExpanded(true);
        }

        hasInitialisedFromRoute.current = true;
    }, [pathname, isContextualMenu]);

    /* -------------------------------------------------------
       Build navigation
    -------------------------------------------------------- */
    useEffect(() => {
        const routerNav = (path: string): void => {
            resetErrors();
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }): INavLink => ({
            key: path,
            name,
            url: `#${path}`,
            onClick: () => routerNav(path)
        });

        /* -------- CONTEXTUAL MENU -------- */
        if (isContextualMenu && selectedLabel) {
            const links: INavLink[] = [
                { name: 'Home', path: '/' },
                { name: 'Welcome', path: '/a2/welcome' },
                { name: selectedLabel, path: pathname }
            ].map(NamePathToNavLink);

            setNavs(links);
            return;
        }

        /* -------- GLOBAL MENU -------- */
        const links: INavLink[] = [
            { name: 'Home', path: '/' },
            { name: 'Welcome', path: '/a2/welcome' },
            { name: 'Insights', path: '/a2/insights' },
            { name: 'Ask Cloe', path: '/a2/ai-chats' },
            { name: 'Vision', path: '/a2/visions' },
            { name: 'Objectives', path: '/a2/objectives' }
        ].map(NamePathToNavLink);

        const canViewProjects = up.UserCanViewProjectEntities();
        const canViewDirectorate = up.UserCanViewDirectorateEntities();

        if (up.UserIsSystemAdmin() || canViewProjects || canViewDirectorate) {
            links.push({
                key: 'management',
                name: 'Management',
                url: null,
                isExpanded: portfolioExpanded,
                onClick: () => setPortfolioExpanded(prev => !prev),
                links: [
                    canViewDirectorate && { name: 'Portfolios', path: '/a2/portfolios' },
                    canViewDirectorate && { name: 'Programmes', path: '/a2/programmes' },
                    canViewProjects && { name: 'Projects', path: '/a2/projects' }
                ].filter(Boolean).map(NamePathToNavLink)
            });
        }

        if (up.UserIsSystemAdmin()) {
            links.push({
                key: 'settings',
                name: 'Settings',
                url: null,
                isExpanded: settingsExpanded,
                onClick: () => setSettingsExpanded(prev => !prev),
                links: [
                    { name: 'Benefit types', path: '/a2/settings/benefit-types' },
                    { name: 'Reporting frequencies', path: '/a2/settings/reporting-frequencies' },
                    { name: 'Risk types', path: '/a2/settings/risk-types' },
                    { name: 'Risk discussion forums', path: '/a2/settings/risk-discussion-forums' },
                    { name: 'Units', path: '/a2/settings/units' },
                    { name: 'Thresholds', path: '/a2/settings/thresholds' },
                    { name: 'Threshold appetites', path: '/a2/settings/threshold-appetites' }
                ].map(NamePathToNavLink)
            });
        }

        setNavs(links);
    }, [
        up,
        history,
        onLinkClicked,
        resetErrors,
        pathname,
        isContextualMenu,
        selectedLabel,
        portfolioExpanded,
        settingsExpanded
    ]);

    /* -------------------------------------------------------
       Render
    -------------------------------------------------------- */
    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                {/* Logo */}
                <div
                    style={{
                        width: '100%',
                        paddingTop: 0,
                        paddingBottom: '1px',
                        paddingLeft: '15px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-ManageProjects.png`}
                        alt="Manage Projects"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </div>

                {/* Logged-in user */}
                <LoggedUserInfoForMenu />

                {/* Contextual header */}
                {isContextualMenu && entityDescriptionForMenu && (
                    <div
                        style={{
                            backgroundColor: '#1A619F',
                            color: 'white',
                            padding: '10px 15px',
                            margin: '5px 10px 10px',
                            borderRadius: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px'
                        }}
                    >
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine1} />
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine2} />
                    </div>
                )}

                {/* Navigation */}
                <Nav
                    selectedKey={pathname}
                    groups={[{ links: navs }]}
                    onRenderLink={(lnk) =>
                        _onRenderNavLink(
                            lnk,
                            totalPortfolios,
                            totalProgrammes,
                            totalProjects
                        )
                    }
                />
            </div>
        </ThemeProvider>
    );
};
